/**
 * Finding the record an Instagram handle belongs to, and recording it there.
 *
 * Two jobs, both in service of the unrecognised list on the Channel stats
 * screen: resolving who a handle already belongs to, and writing it onto a
 * record that is missing it.
 *
 * ## Attribution is resolved here, not read from the mention
 *
 * The collector stores the match it made, but the screen re-resolves against
 * the records as they are now. Otherwise linking a handle would leave the row
 * saying "not recognised" until the next nightly run — the user would do the
 * work and see nothing happen. The stored fields are a record of what the
 * collector knew; this is what is true.
 *
 * ## Everything is loaded, including unverified leads
 *
 * Leads used to be queried by name prefix instead, to avoid loading 500
 * documents. That was the wrong trade, and the real data showed why: 469 of
 * 513 leads are `unverified`, so the pipeline is mostly the pool a stranger who
 * tags us would already be in — and a prefix search over company names cannot
 * find them from a handle. Linking `@msmg.troms` meant guessing that the record
 * was called "Midnight Sun Mountain Guides".
 *
 * So all four collections load whole and the search matches anywhere in the
 * name. Six hundred documents, read once when the picker opens, on a screen the
 * leads workspace already loads the same leads for.
 */
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'
import { EVENT_COLLECTIONS } from '@/features/events/repository/eventsRepository'
import type { MentionMatchKind } from '@/types/mentions'

export interface HandleTarget {
  kind: MentionMatchKind
  id: string
  name: string
  /** The handle already on the record, if any. */
  instagram: string
  /** One line of context so two people with similar names are tellable apart. */
  detail: string
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

/** Lowercased, @ stripped, and nothing else — the same rule the collector uses. */
export const handleKey = (h: string): string => h.trim().toLowerCase().replace(/^@/, '')

/**
 * The records that can hold a handle, minus leads.
 *
 * Loaded once when the picker opens rather than kept live: this is a lookup
 * during a deliberate action, not a screen that has to stay current.
 */
export async function loadHandleTargets(): Promise<HandleTarget[]> {
  const [people, partners, events, leads] = await Promise.all([
    getDocs(query(collection(db, EVENT_COLLECTIONS.people))),
    getDocs(query(collection(db, CRM_COLLECTIONS.partners))),
    getDocs(query(collection(db, EVENT_COLLECTIONS.events))),
    getDocs(query(collection(db, CRM_COLLECTIONS.leads))),
  ])

  const out: HandleTarget[] = []

  for (const d of people.docs) {
    const v = d.data()
    out.push({
      kind: 'ambassador',
      id: d.id,
      name: str(v.name) || d.id,
      instagram: str(v.instagram),
      detail: [str(v.role), (v.countries as string[] | undefined)?.join('/')]
        .filter(Boolean)
        .join(' · '),
    })
  }
  for (const d of partners.docs) {
    const v = d.data()
    out.push({
      kind: 'partner',
      id: d.id,
      name: str(v.name) || d.id,
      instagram: str(v.instagram),
      detail: [str(v.kind), str(v.country)].filter(Boolean).join(' · '),
    })
  }
  for (const d of events.docs) {
    const v = d.data()
    out.push({
      kind: 'event',
      id: d.id,
      name: str(v.name) || d.id,
      instagram: str(v.instagram),
      detail: [str(v.country), str(v.startDate)].filter(Boolean).join(' · '),
    })
  }

  for (const d of leads.docs) {
    const v = d.data()
    out.push({
      kind: 'lead',
      id: d.id,
      name: str(v.name) || d.id,
      instagram: str(v.instagram),
      // Stage first, because "unverified" is the common case and reads as a
      // reason to link rather than a reason to hesitate — somebody who tags us
      // is exactly the evidence that moves a lead out of Step 0.
      detail: [str(v.stage), str(v.country), str(v.website).replace(/^https?:\/\//, '')]
        .filter(Boolean)
        .join(' · '),
    })
  }

  return out.sort((a, b) => a.name.localeCompare(b.name))
}


/** Every handle already recorded, so a mention can be attributed live. */
export async function loadHandleIndex(): Promise<Map<string, HandleTarget>> {
  const withHandle = (c: string) =>
    getDocs(query(collection(db, c), where('instagram', '!=', '')))

  const [people, partners, events, leads] = await Promise.all([
    withHandle(EVENT_COLLECTIONS.people),
    withHandle(CRM_COLLECTIONS.partners),
    withHandle(EVENT_COLLECTIONS.events),
    withHandle(CRM_COLLECTIONS.leads),
  ])

  const index = new Map<string, HandleTarget>()
  const add = (kind: MentionMatchKind, id: string, v: Record<string, unknown>) => {
    const k = handleKey(str(v.instagram))
    // First writer wins, and the collector records the conflict separately —
    // this map only needs to name somebody, not adjudicate.
    if (k && !index.has(k)) {
      index.set(k, { kind, id, name: str(v.name) || id, instagram: str(v.instagram), detail: '' })
    }
  }

  for (const d of people.docs) add('ambassador', d.id, d.data())
  for (const d of partners.docs) add('partner', d.id, d.data())
  for (const d of events.docs) add('event', d.id, d.data())
  for (const d of leads.docs) add('lead', d.id, d.data())

  return index
}

const COLLECTION_OF: Record<MentionMatchKind, string> = {
  ambassador: EVENT_COLLECTIONS.people,
  partner: CRM_COLLECTIONS.partners,
  event: EVENT_COLLECTIONS.events,
  lead: CRM_COLLECTIONS.leads,
}

/**
 * Record a handle on the chosen record.
 *
 * A merge write of one field. Nothing is written to the mention itself — that
 * collection is collector-owned, and the screen resolves attribution from the
 * records anyway, so the row updates the moment this returns.
 */
export async function assignHandle(
  target: Pick<HandleTarget, 'kind' | 'id'>,
  handle: string,
): Promise<void> {
  await setDoc(
    doc(db, COLLECTION_OF[target.kind], target.id),
    { instagram: handleKey(handle) },
    { merge: true },
  )
}
